import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Autocomplete, Box, Button, DataTable, FormField, useCommonComponentContext, IPropListContainer, RoleHelpers } from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import MessageTemplateService from "../../services/message/MessageTemplate.service";

import { Constants } from "../../constants/constants";
import { IRecordMessageTemplate } from "../../commons/interfaces";
import { MessageTemplateType, Mode } from "../../constants/enums";
import useServiceCodenames from "../../hooks/messageTemplate/useServiceCodenames";
import useTenantCodenames from "../../hooks/messageTemplate/useTenantCodenames";


export interface IRequestGetPagedMessageTemplate {
    type?: string;
    searchText?: string;
    pageNumber: number;
    pageSize: number;
    orderby?: string;
    totalCount?: number;

    tenantCode?: string;
    serviceCode?: string;
}
export interface IMessageTemplateListContainerProps extends Omit<IPropListContainer<IRequestGetPagedMessageTemplate>, "onNavigate"> {
    onNavigate: (data?: { id?: string; mode?: Mode, tenantCode?: string; serviceCode?: string; }) => void;
}

interface IModel {
    requestData?: IRequestGetPagedMessageTemplate;
    requestDataTemp?: IRequestGetPagedMessageTemplate;
}

const MessageTemplateListContainer = (props: IMessageTemplateListContainerProps) => {
    const Strings = useStrings();

    const { language, userInfo, tenantCode, serviceCode, onError, onSuccess, onHideLoading, onShowLoading } = useCommonComponentContext();

    const [model, setModel] = useState<IModel>({} as IModel);
    const [rows, setRows] = useState<IRecordMessageTemplate[]>([]);
    const organizationId = userInfo?.userProfile?.organizationId;
    const isServiceAdmin = RoleHelpers.isServiceAdmin(userInfo?.userProfile?.roleType);

    const MessageTemplateTypeData = [
        {
            code: `${MessageTemplateType.Email}`,
            name: Strings.Common.EMAIL,
        },
        {
            code: `${MessageTemplateType.SMS}`,
            name: Strings.Common.SMS,
        },
        {
            code: `${MessageTemplateType.Notification}`,
            name: Strings.Common.NOTIFICATION,
        },
    ];

    useEffect(() => {
        const newDataReq: IRequestGetPagedMessageTemplate = {
            totalCount: props.requestGetPaged?.totalCount,
            orderby: props.requestGetPaged?.orderby || model?.requestData?.orderby,
            pageSize: props.requestGetPaged?.pageSize || model?.requestData?.pageSize,
            pageNumber: props.requestGetPaged?.pageNumber || model?.requestData?.pageNumber,

            type: props.requestGetPaged?.type || model?.requestData?.type,
            searchText: props.requestGetPaged?.searchText || model?.requestData?.searchText,
            tenantCode: props.requestGetPaged?.tenantCode || model?.requestData?.tenantCode,
            serviceCode: props.requestGetPaged?.serviceCode || model?.requestData?.serviceCode,
        };

        getPaged(newDataReq);
    }, [props.requestGetPaged, userInfo?.userProfile?.organizationId]);

    const getPaged = async (requestData?: IRequestGetPagedMessageTemplate) => {
        let tenantCodeRequest = tenantCode;
        let serviceCodeRequest = serviceCode;
        if (isServiceAdmin) {
            tenantCodeRequest = requestData?.tenantCode ?? undefined;
            serviceCodeRequest = requestData?.serviceCode ?? undefined;
        }

        try {
            onShowLoading();

            const pageSize = requestData?.pageSize || Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(requestData?.pageNumber || 1, pageSize, requestData?.totalCount || 0);

            const request: any = {
                pageSize: pageSize,
                pageNumber: pageNumber,
                searchText: requestData?.searchText,
                type: Helpers.isNullOrEmpty(requestData?.type) ? undefined : Number(requestData?.type),

                tenantCode: tenantCodeRequest,
                serviceCode: serviceCodeRequest,
                organizationId: organizationId || "0",
            };

            const result = await MessageTemplateService.getPaged(Helpers.handleFormatParams(request));

            setRows([...(result.items || [])]);

            setModel({
                requestData: { ...request, totalCount: result.totalCount },
                requestDataTemp: { ...request, totalCount: result.totalCount },
            });

            let query: string = `?pageNumber=${pageNumber}&pageSize=${request?.pageSize}`;
            if (!Helpers.isNullOrEmpty(request?.searchText)) {
                query += `&searchText=${request?.searchText}`;
            }
            if (!Helpers.isNullOrEmpty(request?.type)) {
                query += `&type=${request?.type}`;
            }
            if (!Helpers.isNullOrEmpty(request?.tenantCode)) {
                query += `&tenantCode=${request?.tenantCode}`;
            }
            if (!Helpers.isNullOrEmpty(request?.serviceCode)) {
                query += `&serviceCode=${request?.serviceCode}`;
            }

            props.onGetPaged({ query, totalCount: result.totalCount || 0 });
        } catch (error) {
            onHideLoading();
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    };
    const { serviceCodenames } = useServiceCodenames({ organizationId });
    const { tenantCodenames } = useTenantCodenames({ organizationId });

    const filterForm = () => (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormField
                        maxLength={255}
                        label={Strings.Common.SEARCH_TEXT}
                        placeholder={Strings.Common.ENTER_SEARCH_TEXT}
                        defaultValue={model.requestDataTemp?.searchText}
                        onBlur={(value) => {
                            setModel({
                                ...model,
                                requestDataTemp: {
                                    ...model.requestDataTemp,
                                    searchText: value,
                                },
                            });
                        }}
                    />
                </Grid>
                {isServiceAdmin && <Grid item xs={12}>
                    <Autocomplete
                        data={tenantCodenames || []}
                        defaultValue={model?.requestDataTemp?.tenantCode}
                        label={Strings.MESSAGE_TEMPLATE.TENANT}
                        placeholder={Strings.MESSAGE_TEMPLATE.SELECT_TENANT}
                        onChange={(value) => {
                            setModel({
                                ...model,
                                requestDataTemp: {
                                    ...model.requestDataTemp,
                                    tenantCode: value,
                                },
                            });
                        }}
                    />
                </Grid>}
                {isServiceAdmin && <Grid item xs={12}>
                    <Autocomplete
                        data={serviceCodenames || []}
                        defaultValue={model?.requestDataTemp?.serviceCode}
                        label={Strings.MESSAGE_TEMPLATE.SERVICE}
                        placeholder={Strings.MESSAGE_TEMPLATE.SELECT_SERVICE}
                        onChange={(value) => {
                            setModel({
                                ...model,
                                requestDataTemp: {
                                    ...model.requestDataTemp,
                                    serviceCode: value,
                                },
                            });
                        }}
                    />
                </Grid>}
                <Grid item xs={12}>
                    <Autocomplete
                        data={MessageTemplateTypeData || []}
                        label={Strings.MESSAGE_TEMPLATE.TYPE}
                        defaultValue={model.requestDataTemp?.type}
                        placeholder={Strings.MESSAGE_TEMPLATE.SELECT_TYPE}
                        onChange={(value) => {
                            setModel({
                                ...model,
                                requestDataTemp: {
                                    ...model.requestDataTemp,
                                    type: value,
                                },
                            });
                        }}
                    />
                </Grid>
            </Grid>
        </Box>
    );

    const handleDelete = async (id: string) => {
        Helpers.showConfirmAlert(Strings.MESSAGE_TEMPLATE.CONFIRM_DELETE, async () => {
            try {
                onShowLoading();

                await MessageTemplateService.delete(id);
                await getPaged({ ...model.requestData, pageNumber: 1 });

                onSuccess(Strings.MESSAGE_TEMPLATE.DELETE_SUCCESS);
            } catch (error) {
                onHideLoading();
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        });
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end", mb: 3 }}>
                <Button
                    onClick={() => {
                        props.onNavigate({ id: undefined, mode: Mode.Create, tenantCode: model?.requestData?.tenantCode, serviceCode: model?.requestData?.serviceCode });
                    }}
                >
                    {Strings.Common.ADD_NEW}
                </Button>
            </Box>
            <Card>
                <Box p={2}>
                    <DataTable
                        isShowIndex
                        totalCount={model.requestData?.totalCount}
                        pageNumber={model.requestData?.pageNumber || 1}
                        rowPerPage={model.requestData?.pageSize || Constants.ROW_PER_PAGE}
                        onChangePageSize={(pageSize) => getPaged({ ...model.requestData, pageSize })}
                        onChangePageNumber={(pageNumber) => getPaged({ ...model.requestData, pageNumber })}
                        searchText={model.requestData?.searchText}
                        placeholderSearch={Strings.Common.SEARCH_TEXT}
                        onSearch={(val) => getPaged({ ...model.requestData, pageNumber: 1, searchText: val })}
                        onSearchChange={(val) => {
                            setModel({
                                ...model,
                                requestData: {
                                    ...model.requestData,
                                    searchText: val,
                                },
                            });
                        }}
                        filterForm={filterForm()}
                        onFilter={() => {
                            getPaged({ ...model.requestDataTemp, pageNumber: 1 });
                        }}
                        onCloseFilter={() => {
                            const newData = { ...model.requestData };
                            setModel({ ...model, requestDataTemp: newData });
                        }}
                        onReset={() => {
                            getPaged({
                                ...model.requestData,
                                tenantCode: undefined,
                                serviceCode: undefined,
                                pageNumber: 1,
                            });
                        }}
                        table={{
                            columns: [
                                {
                                    Header: Strings.MESSAGE_TEMPLATE.TYPE,
                                    accessor: "type",
                                    width: "100px",
                                    Cell: ({ value }: any) => (
                                        <>{MessageTemplateTypeData.find((el) => Number(el.code) === Number(value))?.name || ""}</>
                                    ),
                                },
                                {
                                    Header: Strings.MESSAGE_TEMPLATE.NAME,
                                    accessor: "templateName",
                                },
                                {
                                    Header: Strings.MESSAGE_TEMPLATE.TITLE,
                                    accessor: "title",
                                    Cell: ({ value }: any) => <>{Helpers.renderValueByLanguage({ data: value, currentLanguage: language })}</>,
                                },
                            ],
                            rows: [...(rows || [])],
                        }}
                        actionList={[
                            {
                                key: "detail",
                                iconName: "view",
                                title: Strings.Common.VIEW_DETAIL,
                                callback: (row) => {
                                    props.onNavigate({ id: row?.id, mode: Mode.View });
                                },
                            },
                            {
                                key: "edit",
                                iconName: "edit",
                                title: Strings.Common.EDIT,
                                callback: (row) => {
                                    props.onNavigate({ id: row?.id, mode: Mode.Update });
                                },
                            },
                            {
                                key: "delete",
                                iconName: "delete",
                                title: Strings.Common.DELETE,
                                callback: (row) => handleDelete(row.id),
                            },
                        ]}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default MessageTemplateListContainer;
